import { datadogLogs } from '@datadog/browser-logs'
import { useEffect, useState } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { axiosWithRetry } from './usePostCallback'
import { useOptionalQuestionnaireParams } from './useQuestionnaireParams'

export interface SurveyLocales {
  primaryLanguage: string
  availableLanguages: string[]
}

interface BackendResponse {
  survey_id: string
  primary_language: string
  available_languages: string[]
}

interface Data {
  surveyLocales: SurveyLocales | undefined
  loading: boolean
  error: boolean
}

const useSurveyLocales = (): Data => {
  const questionnaireParams = useOptionalQuestionnaireParams()
  const [data, setData] = useState<Data>({
    surveyLocales: undefined,
    loading: true,
    error: false
  })

  useEffect(() => {
    const fetchSurveyLocales = async (surveyId: string) => {
      const url = coreRespondentUrl(`/surveys/${surveyId}/locales`)

      try {
        const { data: response }: { data: BackendResponse } =
          await axiosWithRetry({
            method: 'get',
            url,
            timeout: 30000
          })
        setData({
          surveyLocales: {
            primaryLanguage: response.primary_language,
            availableLanguages: response.available_languages
          },
          loading: false,
          error: false
        })
      } catch (error) {
        datadogLogs.logger.error(
          'Network Failure: Could not fetch survey locales',
          { error, surveyId }
        )
        setData({
          surveyLocales: undefined,
          loading: false,
          error: true
        })
      }
    }

    if (questionnaireParams) {
      fetchSurveyLocales(questionnaireParams.surveyId)
    }
  }, [questionnaireParams])

  return data
}

export default useSurveyLocales
